import React from 'react';
import { Card } from 'antd';
import {
  ClockCircleOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  CarOutlined,
  SendOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const COLORS = {
  success: '#52C41A',
  warning: '#FAAD14',
  danger: '#FF4D4F',
  brand: '#00A651',
  blue: '#1890ff',
  orange: '#FA8C16',
};

/**
 * Module 2: Fulfillment Pipeline
 * Horizontal step-based pipeline showing order flow with counts and avg times
 */
const FulfillmentPipeline = ({ data }) => {
  const { t } = useTranslation();
  if (!data) return null;

  const nodes = [
    {
      label: t('dashboard.pipeline.waiting'),
      icon: <ClockCircleOutlined />,
      count: data.waitingOrders,
      metrics: [
        {
          label: t('dashboard.pipeline.avgWait'),
          value: data.avgWaitTime,
          unit: t('dashboard.min'),
          warning: data.avgWaitTime > 10,
        },
      ],
      color: COLORS.warning,
      bg: '#fffbe6',
    },
    {
      label: t('dashboard.pipeline.pickAndPack'),
      icon: <ShoppingCartOutlined />,
      count: data.pickingOrders,
      // 指标顺序对齐参考图：Avg Orders / Task → Avg Pick & Pack Time / Task → Avg Pick & Pack Time / Order
      metrics: [
        { label: t('dashboard.pipeline.avgOrdersPerTask'), value: data.avgOrdersPerTask },
        { label: t('dashboard.pipeline.avgPickPackPerTask'), value: data.avgPickPackPerTask, unit: t('dashboard.min') },
        { label: t('dashboard.pipeline.avgPickPackPerOrder'), value: data.avgPickPackPerOrder, unit: t('dashboard.min') },
      ],
      color: COLORS.blue,
      bg: '#e6f7ff',
    },
    {
      label: t('dashboard.pipeline.readyToDelivery'),
      icon: <CheckCircleOutlined />,
      count: data.completedOrders,
      metrics: [
        { label: t('dashboard.pipeline.avgWaitingForDelivery'), value: data.avgWaitingForDelivery, unit: t('dashboard.min') },
      ],
      color: COLORS.success,
      bg: '#f6ffed',
    },
    {
      // In Delivery：已派单、骑手/配送中状态的订单，展示 Avg Delivery Time
      // （配送耗时 = 出库完成时间 − 骑手取货时间，实际时间戳计算）；色系对齐原型为橙色。
      label: t('dashboard.pipeline.inDelivery'),
      icon: <SendOutlined />,
      count: data.inDeliveryOrders,
      metrics: [
        { label: t('dashboard.pipeline.avgDeliveryTime'), value: data.avgDeliveryTime, unit: t('dashboard.min') },
      ],
      color: COLORS.orange,
      bg: '#fff7e6',
    },
    {
      // Outbound Complete：履约链路末端节点，展示当日出库完成订单量与 Avg E2E Time
      // （端到端耗时 = 出库完成时间 − 订单创建时间，实际时间戳计算；原右上角 End-to-End 汇总已移除）。
      label: t('dashboard.pipeline.outboundComplete'),
      icon: <CarOutlined />,
      count: data.outboundCompletedOrders,
      metrics: [
        { label: t('dashboard.pipeline.avgE2ETime'), value: data.avgE2ETime, unit: t('dashboard.min') },
      ],
      color: COLORS.brand,
      bg: '#e6f9f0',
    },
  ];

  return (
    <Card
      title={t('dashboard.pipeline.title')}
      styles={{ body: { padding: '16px 24px' } }}
    >
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
        {nodes.map((node, index) => (
          <React.Fragment key={node.label}>
            {/* Pipeline Node */}
            <div
              style={{
                flex: 1,
                background: node.bg,
                borderRadius: 8,
                padding: '16px 12px',
                border: `1px solid ${node.color}22`,
                position: 'relative',
                minWidth: 0,
              }}
            >
              {/* Header with icon */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <span style={{ fontSize: 18, color: node.color }}>{node.icon}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>{node.label}</span>
              </div>

              {/* Count - big number on top */}
              <div style={{ fontSize: 36, fontWeight: 'bold', color: node.color, lineHeight: 1, marginBottom: 8 }}>
                {typeof node.count === 'number' ? node.count.toLocaleString() : node.count}
              </div>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>{t('dashboard.orders')}</div>

              {/* Unified metrics rows（五卡片同一渲染路径：标签 13px/#333/600、数值 18px 节点主色 bold、单位 12px 节点主色） */}
              {node.metrics.map((m) => (
                <div
                  key={m.label}
                  style={{
                    fontSize: 13,
                    color: m.warning ? COLORS.danger : '#333',
                    fontWeight: 600,
                    marginBottom: 6,
                  }}
                >
                  {m.label}:{' '}
                  <b style={{ color: m.warning ? COLORS.danger : node.color, fontSize: 18 }}>
                    {Number(m.value || 0).toFixed(2)}
                  </b>
                  {m.unit && (
                    <span
                      style={{
                        color: m.warning ? COLORS.danger : node.color,
                        fontSize: 12,
                        fontWeight: 600,
                        marginLeft: 2,
                      }}
                    >
                      {m.unit}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Arrow between nodes */}
            {index < nodes.length - 1 && (
              <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px', color: '#d9d9d9' }}>
                <ArrowRightOutlined style={{ fontSize: 20 }} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </Card>
  );
};

export default FulfillmentPipeline;
